import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { sanFranciscanSettings } from '$lib/PersistedState.svelte';
import { fahrenheitToCelsius } from '$lib/RLogUtils';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class ModbusClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModbusClientError';
  }
}

interface SanFranciscanReading {
  bean: number;
  inlet: number | null;
  exhaust: number | null;
  setpoint: number | null;
  burner: number | null;
}

export class SanFranciscanModbusDevice extends BaseDevice {
  type: DeviceType = DeviceType.SAN_FRANCISCAN;
  private burnerPower: number | null = null;
  private setpoint: number | null = null;

  // Register mappings for Watlow PM6 controller
  // Each temperature value spans two 16-bit registers (32-bit IEEE 754 float)
  private static readonly REGISTERS = {
    // Analog Inputs (32-bit floats)
    BEAN_TEMP: 360, // Registers 360 (LSW) + 361 (MSW)
    INLET_TEMP: 362, // Registers 362 (LSW) + 363 (MSW)
    EXHAUST_TEMP: 364, // Registers 364 (LSW) + 365 (MSW)

    // Setpoints (32-bit floats)
    SETPOINT_1: 2640, // Registers 2640 (LSW) + 2641 (MSW)

    // Control values (32-bit floats)
    BURNER_POWER: 2650 // Registers 2650 (LSW) + 2651 (MSW)
  };

  protected async onConnect(): Promise<boolean> {
    try {
      await invoke('connect_modbus', {
        host: sanFranciscanSettings.current.host,
        port: this.getModbusPort()
      });
      return true;
    } catch (error) {
      logger.error(`Failed to connect to San Franciscan Modbus device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_modbus', {
      host: sanFranciscanSettings.current.host,
      port: this.getModbusPort()
    }).catch((error) => logger.warn(`Disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      const readings = await this.readAllValues(sanFranciscanSettings.current.host);

      this.setpoint = readings.setpoint;
      this.burnerPower = readings.burner;

      const bean = fahrenheitToCelsius(readings.bean);
      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_SANFRANCISCAN_MODBUS_READINGS', { readings, bean });
      }
      // San Franciscan returns temperatures in °F, convert to °C for consistency
      this.localReadings = [
        bean,
        this.tcSettings?.[1].active && readings.inlet ? fahrenheitToCelsius(readings.inlet) : null,
        this.tcSettings?.[2].active && readings.exhaust
          ? fahrenheitToCelsius(readings.exhaust)
          : null,
        null // TC4 not used for San Franciscan
      ];
    } catch (error) {
      if (error instanceof ModbusClientError) {
        logger.error(`Modbus error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.burnerPower;
  }

  public getSetpoint(): number | null {
    return this.setpoint;
  }

  /**
   * Write setpoint to the device
   * @param temperature Temperature in °F
   */
  public async writeSetpoint(temperature: number): Promise<void> {
    try {
      const floatBytes = SanFranciscanModbusDevice.floatToBytes(temperature);

      await invoke('write_multiple_registers', {
        host: sanFranciscanSettings.current.host,
        port: this.getModbusPort(),
        reg: SanFranciscanModbusDevice.REGISTERS.SETPOINT_1,
        values: [floatBytes.lsw, floatBytes.msw]
      });

      logger.info(`Setpoint written: ${temperature}°F`);
    } catch (error) {
      logger.error(`Failed to write setpoint: ${error}`);
      throw error;
    }
  }

  private async readAllValues(host: string): Promise<SanFranciscanReading> {
    try {
      // Read all temperature and control values in parallel
      const [beanReading, inletReading, exhaustReading, setpointReading, burnerReading] =
        await Promise.all([
          this.readFloatRegister(host, SanFranciscanModbusDevice.REGISTERS.BEAN_TEMP),
          this.readFloatRegister(host, SanFranciscanModbusDevice.REGISTERS.INLET_TEMP),
          this.readFloatRegister(host, SanFranciscanModbusDevice.REGISTERS.EXHAUST_TEMP),
          this.readFloatRegister(host, SanFranciscanModbusDevice.REGISTERS.SETPOINT_1),
          this.readFloatRegister(host, SanFranciscanModbusDevice.REGISTERS.BURNER_POWER)
        ]);

      return {
        bean: beanReading,
        inlet: inletReading,
        exhaust: exhaustReading,
        setpoint: setpointReading,
        burner: burnerReading
      };
    } catch (err: any) {
      const regex = /error creating modbus client/i;
      if (regex.test(err)) {
        throw new ModbusClientError('Could not create Modbus client');
      } else {
        logger.warn(`Modbus reading error: ${err}`);
      }

      return {
        bean: 0,
        inlet: null,
        exhaust: null,
        setpoint: null,
        burner: null
      };
    }
  }

  /**
   * Read a 32-bit float value from two consecutive Modbus registers
   * @param host Modbus host
   * @param startRegister Starting register address (LSW)
   * @returns Float value
   */
  private async readFloatRegister(host: string, startRegister: number): Promise<number> {
    const results: number[] = await invoke('read_modbus', {
      host,
      port: this.getModbusPort(),
      reg: startRegister,
      count: 2
    });

    return SanFranciscanModbusDevice.convertToFloat(results[0], results[1]);
  }

  /**
   * Convert two 16-bit Modbus registers to a 32-bit IEEE 754 float
   * San Franciscan uses Low/High word order (LSW first, MSW second)
   * IEEE 754 format: 0x42960000 = 75.0°F
   */
  static convertToFloat(lsw: number, msw: number): number {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);

    // Low/High word order: LSW first, MSW second
    // For IEEE 754, we need to combine the bytes correctly
    view.setUint16(0, lsw, true); // Little endian for LSW
    view.setUint16(2, msw, true); // Little endian for MSW

    return view.getFloat32(0, true); // Little endian
  }

  /**
   * Convert a float to two 16-bit registers for Modbus writing
   * @param value Float value to convert
   * @returns Object with LSW and MSW values
   */
  static floatToBytes(value: number): { lsw: number; msw: number } {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);

    view.setFloat32(0, value, true); // Little endian

    return {
      lsw: view.getUint16(0, true), // Little endian
      msw: view.getUint16(2, true) // Little endian
    };
  }
}
